/** Theme behaviours as small custom elements. Vanilla JS, no dependencies. */

const hoverQuery = matchMedia('(hover: hover) and (min-width: 1024px)');

/** Article table of contents: one link per H2 of the body (ids added when missing). */
const toc = document.querySelector('[data-article-toc]');
const headings = [...document.querySelectorAll('[data-article-body] h2')];
if (toc && headings.length > 1) {
  for (const heading of headings) {
    heading.id ||= heading.textContent.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const link = document.createElement('a');
    link.href = `#${heading.id}`;
    link.textContent = heading.textContent.trim();
    toc.append(link);
  }
  toc.hidden = false;
}

/** Header: scrolled state, projects mega menu, mobile menu. */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.megaToggle = this.querySelector('[data-mega-toggle]');
    this.burger = this.querySelector('.header__burger');

    const onScroll = () => this.toggleAttribute('data-scrolled', scrollY > 40);
    addEventListener('scroll', onScroll, { passive: true, signal });
    onScroll();

    this.megaToggle?.addEventListener('click', () => this.setMenu(!this.hasAttribute('data-menu-open')), { signal });
    this.megaToggle?.addEventListener('mouseenter', () => hoverQuery.matches && this.setMenu(true), { signal });
    this.addEventListener('mouseleave', () => hoverQuery.matches && this.setMenu(false), { signal });
    this.burger?.addEventListener('click', () => this.setNav(!this.hasAttribute('data-nav-open')), { signal });
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        this.setMenu(false);
        this.setNav(false);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.abort.abort();
  }

  setMenu(open) {
    this.toggleAttribute('data-menu-open', open);
    this.megaToggle?.setAttribute('aria-expanded', open);
  }

  setNav(open) {
    this.toggleAttribute('data-nav-open', open);
    this.burger?.setAttribute('aria-expanded', open);
    if (!open) this.setMenu(false);
  }
}
customElements.define('site-header', SiteHeader);

/** Scholarship bar: shows after scrolling, closing it is remembered. */
class ScholarshipBar extends HTMLElement {
  connectedCallback() {
    if (readStorage('ep_bar_dismissed')) {
      this.remove();
      return;
    }
    this.abort = new AbortController();
    const { signal } = this.abort;
    const onScroll = () => this.toggleAttribute('data-visible', scrollY > 500);
    addEventListener('scroll', onScroll, { passive: true, signal });
    onScroll();
    this.querySelector('[data-close]')?.addEventListener(
      'click',
      () => {
        writeStorage('ep_bar_dismissed', '1');
        this.remove();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.abort?.abort();
  }
}
customElements.define('scholarship-bar', ScholarshipBar);

/** Reveal elements with [data-reveal] once they enter the viewport. */
const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-revealed');
      revealObserver.unobserve(entry.target);
    }
  },
  { rootMargin: '0px 0px -8% 0px' }
);
document.querySelectorAll('[data-reveal]').forEach((element) => revealObserver.observe(element));

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Split an element's text into word spans (keeps inline markup such as <strong>). */
function wrapWords(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  const words = [];
  for (const node of textNodes) {
    const fragment = document.createDocumentFragment();
    for (const part of node.textContent.split(/(\s+)/)) {
      if (!part) continue;
      if (/^\s+$/.test(part)) {
        fragment.append(part);
        continue;
      }
      const span = document.createElement('span');
      span.className = 'word';
      span.textContent = part;
      fragment.append(span);
      words.push(span);
    }
    node.replaceWith(fragment);
  }
  return words;
}

/**
 * Scroll effects from the design: [data-word-reveal] lights its words one by one
 * (soft edge of 8 words, from 25% to full opacity); .buy-circles move in opposite
 * directions (±60px). Skipped entirely with reduced motion.
 */
if (!reducedMotion) {
  const wordBlocks = [...document.querySelectorAll('[data-word-reveal]')].map((el) => ({
    el,
    words: wrapWords(el),
    from: Number(el.dataset.wordFrom || 0.25),
  }));
  const circleBlocks = [...document.querySelectorAll('.buy-circles')];
  let ticking = false;
  const update = () => {
    ticking = false;
    const vh = innerHeight;
    for (const { el, words, from } of wordBlocks) {
      const rect = el.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, (vh * 0.85 - rect.top) / (rect.height + vh * 0.35)));
      const lit = progress * (words.length + 8);
      words.forEach((word, i) => (word.style.opacity = from + (1 - from) * Math.min(1, Math.max(0, (lit - i) / 8))));
    }
    for (const block of circleBlocks) {
      const rect = block.parentElement.getBoundingClientRect();
      const offset = Math.max(-1, Math.min(1, (rect.top + rect.height / 2 - vh / 2) / vh));
      block.style.setProperty('--parallax', `${offset * 60}px`);
    }
  };
  if (wordBlocks.length || circleBlocks.length) {
    addEventListener('scroll', () => ticking || ((ticking = true), requestAnimationFrame(update)), { passive: true });
    addEventListener('resize', update);
    update();
  }
}

/** Dots under a horizontal strip: one per visible page, click to jump, follows the scroll. */
class StripDots extends HTMLElement {
  connectedCallback() {
    this.strip = this.previousElementSibling;
    if (!this.strip) return;
    this.strip.addEventListener('scroll', () => this.sync(), { passive: true });
    new ResizeObserver(() => this.build()).observe(this.strip);
  }

  build() {
    const pages = Math.max(1, Math.ceil(this.strip.scrollWidth / this.strip.clientWidth - 0.05));
    this.hidden = pages < 2;
    this.replaceChildren(
      ...Array.from({ length: pages }, (_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `${this.dataset.label} ${i + 1}`);
        dot.addEventListener('click', () => this.strip.scrollTo({ left: i * this.strip.clientWidth, behavior: 'smooth' }));
        return dot;
      })
    );
    this.sync();
  }

  sync() {
    const max = this.strip.scrollWidth - this.strip.clientWidth;
    const index = max <= 0 ? 0 : Math.round((this.strip.scrollLeft / max) * (this.children.length - 1));
    [...this.children].forEach((dot, i) => dot.toggleAttribute('aria-current', i === index));
  }
}
customElements.define('strip-dots', StripDots);

/** Home slider: crossfade every 6.5s, counter and progress bars (bars jump to a slide). */
class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.bars = [...this.querySelectorAll('[data-slide-bar]')];
    this.counter = this.querySelector('[data-slide-counter]');
    this.bars.forEach((bar, i) => bar.addEventListener('click', () => this.go(i)));
    this.go(0);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  go(index) {
    clearTimeout(this.timer);
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === this.index));
    this.bars.forEach((bar, i) => {
      bar.toggleAttribute('data-active', i === this.index);
      bar.toggleAttribute('data-done', i < this.index);
    });
    if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
    if (!reducedMotion && this.slides.length > 1) this.timer = setTimeout(() => this.go(this.index + 1), 6500);
  }
}
customElements.define('hero-slider', HeroSlider);

/** Testimonials: the active quote rotates every 5s; hovering one makes it active. */
class TestimonialRotator extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-testimonial]')];
    this.items.forEach((item, i) => item.addEventListener('mouseenter', () => this.go(i)));
    this.go(0);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  go(index) {
    clearTimeout(this.timer);
    this.index = index % this.items.length;
    this.items.forEach((item, i) => item.toggleAttribute('data-active', i === this.index));
    if (!reducedMotion && this.items.length > 1) this.timer = setTimeout(() => this.go(this.index + 1), 5000);
  }
}
customElements.define('testimonial-rotator', TestimonialRotator);

/** Arrow buttons for a strip: [data-strip-prev]/[data-strip-next] in the same section scroll [data-strip]. */
for (const section of document.querySelectorAll('section:has([data-strip])')) {
  const strip = section.querySelector('[data-strip]');
  const step = () => strip.firstElementChild?.getBoundingClientRect().width + 16 || strip.clientWidth;
  section.querySelector('[data-strip-prev]')?.addEventListener('click', () => strip.scrollBy({ left: -step(), behavior: 'smooth' }));
  section.querySelector('[data-strip-next]')?.addEventListener('click', () => strip.scrollBy({ left: step(), behavior: 'smooth' }));
}

/** Variant pills: keep the price, the fee, the variant text and the URL in sync with the picked date/place. */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const radio = event.target.closest('input[name="id"]');
      if (!radio) return;
      const form = this.closest('form');
      const price = form?.querySelector('[data-price]');
      if (price) price.textContent = radio.dataset.price;
      const fee = form?.querySelector('[data-fee]');
      if (fee) fee.textContent = radio.dataset.fee;
      for (const text of this.querySelectorAll('[data-variant-text]')) text.hidden = text.dataset.variantText !== radio.value;
      const url = new URL(location.href);
      url.searchParams.set('variant', radio.value);
      history.replaceState(history.state, '', url);
    });
  }
}
customElements.define('variant-picker', VariantPicker);

/**
 * Management fee: in the cart every project line says which fee line it needs (data-fee-wants,
 * computed in Liquid) and every fee line what it is (data-fee-has). When the two lists differ the
 * fee lines are rebuilt with the Ajax API and the page reloads once with ?fee: if that fails the
 * cart stays as it is, checkout included (never block a sale on the fee).
 */
const feeCart = document.querySelector('[data-fee-cart]');
if (feeCart && !new URLSearchParams(location.search).has('fee')) {
  const wants = [...feeCart.querySelectorAll('[data-fee-wants]')];
  const has = [...feeCart.querySelectorAll('[data-fee-has]')];
  const signature = (lines, attr) => lines.map((line) => `${line.getAttribute(attr)}:${line.dataset.feeLabel}`).sort().join('|');
  if (signature(wants, 'data-fee-wants') !== signature(has, 'data-fee-has')) {
    const { feeProperty, addUrl, updateUrl } = feeCart.dataset;
    const post = (url, body) =>
      fetch(`${url}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    feeCart.querySelector('[name="checkout"]')?.setAttribute('disabled', '');
    (async () => {
      if (has.length) await post(updateUrl, { updates: Object.fromEntries(has.map((line) => [line.dataset.feeKey, 0])) });
      const items = wants.map((line) => {
        const [id, quantity] = line.dataset.feeWants.split(':').map(Number);
        return { id, quantity, properties: { [feeProperty]: line.dataset.feeLabel } };
      });
      if (items.length) await post(addUrl, { items });
    })().finally(() => location.replace(`${location.pathname}?fee`));
  }
}

/**
 * Birthplace field: a text input with suggestions (the ISTAT comuni as "Nome (PR)" and the foreign
 * states), loaded from assets/comuni.json only the first time the field gets focus, so pages never
 * download the list up front. The datalist holds only the first matches for what is typed (names
 * starting with it first): 8,000 options would freeze a phone while they are built. Any text is
 * still accepted: a comune merged after someone was born is no longer in the list. The datalist
 * has to live in the input's own tree (document or the Shopify Forms shadow root). If the list
 * can't be loaded the field stays a plain text input.
 */
const BIRTHPLACE_SUGGESTIONS = 20;
let birthplaces;

function suggestBirthplaces(input) {
  if (input.dataset.birthplaceReady) return;
  input.dataset.birthplaceReady = 'loading';
  birthplaces ||= fetch(new URL('comuni.json', import.meta.url))
    .then((response) => response.json())
    .then((names) => names.map((name) => [name, name.toLowerCase()]));
  birthplaces
    .then((entries) => {
      // One list per tree: an input the app renders again reuses it.
      const root = input.getRootNode();
      const id = `${input.id || 'birthplace'}-list`.replace(/[^\w-]/g, '-');
      let list = root.getElementById(id);
      if (!list) {
        list = document.createElement('datalist');
        list.id = id;
        (root instanceof ShadowRoot ? root : document.body).append(list);
      }
      input.setAttribute('list', id);
      const update = () => {
        const query = input.value.trim().toLowerCase();
        const starts = [];
        const contains = [];
        if (query.length > 1) {
          for (const [name, lower] of entries) {
            if (lower === query) return list.replaceChildren(); // a suggestion was picked
            if (lower.startsWith(query)) starts.push(name);
            else if (contains.length < BIRTHPLACE_SUGGESTIONS && lower.includes(query)) contains.push(name);
          }
        }
        const matches = [...starts, ...contains].slice(0, BIRTHPLACE_SUGGESTIONS);
        list.replaceChildren(...matches.map((name) => new Option(name)));
      };
      input.addEventListener('input', update);
      update();
      input.dataset.birthplaceReady = 'yes';
    })
    .catch(() => {
      birthplaces = undefined;
      delete input.dataset.birthplaceReady;
    });
}

for (const input of document.querySelectorAll('[data-birthplace]')) {
  input.addEventListener('focus', () => suggestBirthplaces(input));
}

/**
 * Shopify Forms registration form. The app renders in an open shadow root and validates on its own
 * (noValidate), so the listeners sit on the shadow root; it attaches the root late, so they are
 * added on the first focus inside the form (focusin crosses the shadow boundary, retargeted to the
 * host). The marketing consent box is required (the owner's choice: Flow and Messaging only reach
 * subscribed customers; the app has no "required" option for it), checked in the capture phase
 * before the app's own submit handler. The birthplace field is the metafield custom.comune_nascita.
 */
const CONSENT_FIELD_ID = 'emailConsent-true';
const BIRTHPLACE_FIELD_ID = 'custom#comune_nascita';

for (const box of document.querySelectorAll('[data-register-form]')) {
  box.addEventListener('focusin', (event) => {
    const root = event.target.shadowRoot;
    if (!root || root.formReady) return;
    root.formReady = true;
    const onFocus = (target) => {
      if (target?.id === BIRTHPLACE_FIELD_ID) suggestBirthplaces(target);
    };
    onFocus(root.activeElement);
    root.addEventListener('focusin', (event) => onFocus(event.target));
    root.addEventListener('change', (event) => {
      if (event.target.id === CONSENT_FIELD_ID) event.target.setCustomValidity('');
    });
    root.addEventListener(
      'submit',
      (event) => {
        const consent = root.getElementById(CONSENT_FIELD_ID);
        if (!consent || consent.checked) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        consent.setCustomValidity(box.dataset.consentError);
        consent.reportValidity();
      },
      true
    );
  });
}

/** localStorage can throw (private mode, blocked storage): treat that as empty. */
function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: the bar simply comes back on the next page.
  }
}
